import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface EmergencyPackage {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice: number;
  image: string;
  imageFallback: string;
  urgent: boolean;
  preparation_time_minutes: number | null;
}

interface DiscountCode {
  id: string;
  code: string;
  value: number;
  type: string;
  description: string;
  expire_at: string | null;
  minimum_order_amount: number | null;
  active: boolean;
}

interface ErrorPageData {
  packages: EmergencyPackage[];
  discountCode: DiscountCode | null;
  loading: boolean;
  error: string | null;
}

export const useErrorPageData = () => {
  const [data, setData] = useState<ErrorPageData>({
    packages: [],
    discountCode: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchErrorPageData = async () => {
      try {
        // Fetch emergency packages - quick celebration packages
        const { data: packages, error: packagesError } = await supabase
          .from('packages')
          .select(`
            id,
            name,
            description,
            base_price,
            image_url,
            images,
            preparation_time_minutes,
            is_active
          `)
          .eq('is_active', true)
          .or('name.ilike.%celebration%,name.ilike.%quick%,name.ilike.%emergency%,name.ilike.%office%')
          .order('preparation_time_minutes', { ascending: true })
          .limit(2);

        if (packagesError) {
          console.error('Error fetching packages:', packagesError);
        }

        // Fetch active discount codes for error page
        const { data: discounts, error: discountsError } = await supabase
          .from('discounts')
          .select('*')
          .eq('active', true)
          .or('code.eq.FIXME10,description.ilike.%error%,description.ilike.%emergency%')
          .order('created_at', { ascending: false })
          .limit(1);

        if (discountsError) {
          console.error('Error fetching discounts:', discountsError);
        }

        // Process packages data
        const processedPackages: EmergencyPackage[] = (packages || []).map(pkg => ({
          id: pkg.id,
          name: pkg.name,
          description: pkg.description || 'Premium celebration package',
          price: pkg.base_price * 0.9, // 10% discount
          originalPrice: pkg.base_price,
          image: pkg.image_url || (pkg.images as any)?.webp || '/images/celebration/premium-white-gift-box-12-artisan-doughnuts-opened-md.webp',
          imageFallback: pkg.image_url || (pkg.images as any)?.jpeg || '/images/celebration/premium-white-gift-box-12-artisan-doughnuts-opened-md.jpeg',
          urgent: (pkg.preparation_time_minutes || 60) <= 30,
          preparation_time_minutes: pkg.preparation_time_minutes
        }));

        setData({
          packages: processedPackages,
          discountCode: discounts?.[0] || null,
          loading: false,
          error: null
        });

      } catch (error) {
        console.error('Error fetching error page data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load dynamic data'
        }));
      }
    };

    fetchErrorPageData();
  }, []);

  return data;
};